import React, { useState, useRef } from 'react';
import { Form, Input, Button, Upload, DatePicker, message, Radio, Tag } from 'antd';
import { UploadOutlined, FileImageOutlined, DeleteOutlined, PlusOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { checkEdrpouCode, submitPublicRequest } from '../api/publicRequests';
import ContactsSection from './ContactsSection';
import LoadingZoneSelect from './LoadingZoneSelect';

moment.tz.setDefault('Europe/Kiev');

const startRequestTime = parseInt(process.env.REACT_APP_START_REQUEST_TIME || '9');
const endRequestTime = parseInt(process.env.REACT_APP_END_REQUEST_TIME || '16');

const CAR_PLATE_PATTERN = /^[А-ЯҐЄІЇа-яґєії'A-Z]{2}\s?\d{4}\s?[А-ЯҐЄІЇа-яґєії'A-Z]{2}$/i;

const disabledDate = (current) => {
  return current && current < moment().endOf('day');
};

/** Для RangePicker — блокуємо години «end», щоб вони не були раніше «start» на тому ж дні */
const makeRangeDisabledTime = (dates, type) => {
  if (!dates || !dates[0] || !dates[1]) return {};
  const start = dates[0];
  const end   = dates[1];

  if (type === 'end') {
    const sameDay = start.isSame(end, 'day');
    if (sameDay) {
      const startHour = start.hour();
      return {
        disabledHours: () => Array.from({ length: startHour + 1 }, (_, i) => i),
      };
    }
  }
  return {};
};

/** Валідатор для RangePicker */
const makeRangeValidator = (fieldName) => ({
  validator(_, value) {
    if (!value || !value[0] || !value[1]) return Promise.resolve();
    const [start, end] = value;
    if (end.isSameOrBefore(start)) {
      return Promise.reject(new Error('Час завершення має бути пізніше початку'));
    }
    const maxHours = fieldName === 'removalRange' || fieldName === 'deliveryRange' ? 7 * 24 : 24;
    const diffHours = end.diff(start, 'hours');
    if (diffHours > maxHours) {
      if (fieldName === 'removalRange' || fieldName === 'deliveryRange') {
        return Promise.reject(new Error(`Різниця між початком і кінцем ${fieldName === 'removalRange' ? 'демонтажу' : 'монтажу'} не може перевищувати 7 днів`));
      }
      return Promise.reject(new Error('Різниця між початком і кінцем не може перевищувати 24 години'));
    }
    return Promise.resolve();
  },
});

const AdvertisingForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visualFileList, setVisualFileList] = useState([]);
  const [locationFileList, setLocationFileList] = useState([]);
  const [hasAdditionalInfo, setHasAdditionalInfo] = useState(false);
  const [isWorkTime, setIsWorkTime] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Список номерів авто
  const [carPlates, setCarPlates] = useState(['']);

  // useRef щоб синхронно читати вибрані дати в disabledTime
  const deliveryDatesRef = useRef(null);
  const removalDatesRef  = useRef(null);
  // Чи торкнувся користувач поля демонтажу
  const [removalTouched, setRemovalTouched] = useState(false);

  // Реактивне відстеження для disabled-стану пікерів
  const watchedPlacementPeriod = Form.useWatch('placementPeriod', form);
  const watchedDeliveryRange   = Form.useWatch('deliveryRange', form);

  // Пікер монтажу: активний після вибору періоду розміщення
  const isDeliveryEnabled = !!(watchedPlacementPeriod && watchedPlacementPeriod[0] && watchedPlacementPeriod[1]);
  // Пікер демонтажу: активний після вибору монтажу
  const isRemovalEnabled  = !!(watchedDeliveryRange && watchedDeliveryRange[0] && watchedDeliveryRange[1]);

  const disabledDeliveryDate = (current) => {
    if (!current) return false;
    // Блокуємо минулі дати (до сьогодні включно)
    if (current.isBefore(moment().startOf('day'))) return true;

    // Блокуємо дату початку розміщення реклами та пізніші
    const placementPeriod = form.getFieldValue('placementPeriod');
    if (placementPeriod && placementPeriod[0] && current.isSameOrAfter(placementPeriod[0].clone().startOf('day'))) return true;

    // Обмеження: не більше 7 днів від початку монтажу
    if (deliveryDatesRef.current && deliveryDatesRef.current[0] && !deliveryDatesRef.current[1]) {
      const maxEnd = deliveryDatesRef.current[0].clone().add(7, 'days').endOf('day');
      if (current.isAfter(maxEnd)) return true;
    }
    return false;
  };

  const disabledDeliveryTime = (_currentDate, type) => {
    const start = deliveryDatesRef.current && deliveryDatesRef.current[0];
    const end   = deliveryDatesRef.current && deliveryDatesRef.current[1];
    if (type === 'end' && start && end && start.isSame(end, 'day')) {
      return { disabledHours: () => Array.from({ length: start.hour() + 1 }, (_, i) => i) };
    }
    return {};
  };

  const disabledPlacementDate = (current) => {
    if (!current) return false;
    let baseDisabled = current.isBefore(moment().endOf('day'));
    const deliveryRange = form.getFieldValue('deliveryRange');
    
    if (deliveryRange && deliveryRange[1]) {
      if (current.isBefore(deliveryRange[1].clone().startOf('day'))) return true;
    }
    return baseDisabled;
  };

  const disabledRemovalDate = (current) => {
    if (!current) return false;
    if (current.clone().startOf('day').isBefore(moment().startOf('day'))) return true;

    // Демонтаж тільки з НАСТУПНОГО дня після закінчення розміщення
    const placementPeriod = form.getFieldValue('placementPeriod');
    if (placementPeriod && placementPeriod[1]) {
      // isSameOrBefore — блокуємо сам день закінчення і всі дні до
      if (current.clone().startOf('day').isSameOrBefore(placementPeriod[1].clone().startOf('day'))) return true;
    }

    // Обмеження: не більше 7 днів від початку демонтажу
    if (removalDatesRef.current && removalDatesRef.current[0] && !removalDatesRef.current[1]) {
      const maxEnd = removalDatesRef.current[0].clone().add(7, 'days').endOf('day');
      if (current.isAfter(maxEnd)) return true;
    }
    return false;
  };

  const disabledRemovalTime = (_currentDate, type) => {
    const start = removalDatesRef.current && removalDatesRef.current[0];
    const end   = removalDatesRef.current && removalDatesRef.current[1];
    if (type === 'end' && start && end && start.isSame(end, 'day')) {
      return { disabledHours: () => Array.from({ length: start.hour() + 1 }, (_, i) => i) };
    }
    return {};
  };

  React.useEffect(() => {
    const checkWorkTime = () => {
      const now = moment();
      const day = now.day();
      const hour = now.hour();
      const isWorkDay = day >= 1 && day <= 5;
      const isWorkHour = hour >= startRequestTime && hour < endRequestTime;

      if (isWorkDay && isWorkHour) {
        setIsWorkTime(true);
      } else {
        setIsWorkTime(false);
        calculateTimeLeft();
      }
    };

    const calculateTimeLeft = () => {
      const now = moment();
      let nextWorkTime;
      if (now.day() === 6) nextWorkTime = moment().day(8).startOf('day').hour(startRequestTime);
      else if (now.day() === 0) nextWorkTime = moment().day(1).startOf('day').hour(startRequestTime);
      else if (now.hour() >= endRequestTime) nextWorkTime = moment().add(1, 'days').startOf('day').hour(startRequestTime);
      else nextWorkTime = moment().startOf('day').hour(startRequestTime);

      const duration = moment.duration(nextWorkTime.diff(now));
      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.asMinutes()) % 60;
      setTimeLeft(`${hours} год ${minutes} хв`);
    };

    checkWorkTime();
    const interval = setInterval(checkWorkTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleEdrpouBlur = async (e) => {
    const edrpou = e.target.value;
    if (edrpou && edrpou.length >= 8) {
      try {
        const res = await checkEdrpouCode(edrpou);
        if (res && res.success === false) {
          message.warning(res.message || 'Договір не знайдено');
          form.setFields([{ name: 'Account', errors: [res.message || 'Договір не знайдено'] }]);
        } else {
          message.success(res.message || 'Договір знайдено ✓');
          form.setFields([{ name: 'Account', errors: [] }]);
        }
      } catch {
        message.warning('Договір не знайдено');
        form.setFields([{ name: 'Account', errors: ['Договір не знайдено'] }]);
      }
    }
  };

  const handleBeforeUploadVisual = (file) => {
    setVisualFileList((prev) => [...prev, file]);
    return false;
  };

  const handleRemoveVisual = (uid) => {
    setVisualFileList((prev) => prev.filter((f) => f.uid !== uid));
  };

  const handleBeforeUploadLocation = (file) => {
    setLocationFileList((prev) => [...prev, file]);
    return false;
  };

  const handleRemoveLocation = (uid) => {
    setLocationFileList((prev) => prev.filter((f) => f.uid !== uid));
  };

  // ─── Авто: управление списком ─────────────────────────────
  const addCarPlate = () => setCarPlates((prev) => [...prev, '']);
  const removeCarPlate = (idx) => setCarPlates((prev) => prev.filter((_, i) => i !== idx));
  const updateCarPlate = (idx, val) =>
    setCarPlates((prev) => prev.map((v, i) => (i === idx ? val : v)));

  const onFinish = async (values) => {
    if (form.getFieldError('Account').length > 0) {
      message.error('Відправка заблокована: договір не знайдено.');
      return;
    }
    if (visualFileList.length === 0) {
      message.error('Завантажте візуалізацію');
      return;
    }

    if (!executeRecaptcha) {
      message.error('reCAPTCHA ще не готова. Спробуйте пізніше.');
      return;
    }

    // Валідація авто
    const validPlates = carPlates.map((p) => p.trim()).filter(Boolean);
    for (const plate of validPlates) {
      if (!CAR_PLATE_PATTERN.test(plate)) {
        message.error(`Невірний формат номеру: ${plate}. Очікується формат АА 0000 АА`);
        return;
      }
    }

    setLoading(true);
    try {
      const gToken = await executeRecaptcha('advertising_submit');
      
      const payload = {
        ...values,
        type: 'advertising',
        SuServiceSubCategory: '21feea94-2010-4db4-a66a-c989f242f72b',
        'g-recaptcha-response': gToken,
      };

      // hasAdditionalInfoChoice потрапляє в payload через ...values — видаляємо сервісне поле
      // RespHasNotes = true якщо є додаткова інформація АБО фото локації
      delete payload.hasAdditionalInfoChoice;
      payload.RespHasNotes = hasAdditionalInfo === true || locationFileList.length > 0;
      if (payload.isApplicantResponsible) {
         payload.isApplicantResponsible = false;
      }

      if (values.placementPeriod) {
        // Час завжди 00:00 на перший і останній день розміщення
        payload.SuStartWork = values.placementPeriod[0].clone().startOf('day').toISOString();
        payload.SuEndWork   = values.placementPeriod[1].clone().startOf('day').toISOString();
      }

      // Монтаж (RangePicker)
      if (values.deliveryRange && values.deliveryRange[0] && values.deliveryRange[1]) {
        payload.RespEquipmentDeliveryDate = values.deliveryRange[0].clone().minute(0).second(0).toISOString();
        payload.RespEquipmentDeliveryEnd  = values.deliveryRange[1].clone().minute(0).second(0).toISOString();
      }

      // Демонтаж (RangePicker)
      if (values.removalRange && values.removalRange[0] && values.removalRange[1]) {
        payload.RespEquipmentRemovalDate = values.removalRange[0].clone().minute(0).second(0).toISOString();
        payload.RespEquipmentRemovalEnd  = values.removalRange[1].clone().minute(0).second(0).toISOString();
      }

      // Номери авто — через кому
      if (validPlates.length > 0) {
        payload.SuCarNumber = validPlates.join(', ');
      }
      delete payload.deliveryRange;
      delete payload.removalRange;

      const convertBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });

      const allFiles = [...visualFileList, ...locationFileList];
      if (allFiles.length > 0) {
        payload.files = [];
        for (const f of allFiles) {
          const rawFile = f.originFileObj || f;
          const base64Str = await convertBase64(rawFile);
          payload.files.push({ file: base64Str, fileName: f.name });
        }
      }

      await submitPublicRequest(JSON.stringify(payload));
      message.success('Звернення успішно надіслано!');
      form.resetFields();
      setVisualFileList([]);
      setLocationFileList([]);
      setCarPlates(['']);
      setHasAdditionalInfo(false);
    } catch (error) {
      console.error('Submit Error:', error);
      message.error('Сталася помилка при надсиланні.');
    } finally {
      setLoading(false);
    }
  };

  if (!isWorkTime) {
    return (
      <div className="non-work-time-message" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 500, marginBottom: '12px' }}>Створення звернень заблоковано</h2>
        <p style={{ color: '#595959', fontSize: '16px', lineHeight: '28px' }}>
          Подання звернень наразі недоступне.
          <br /> Ми приймаємо їх у <span style={{ fontWeight: 600, color: '#000' }}>будні з {startRequestTime}:00 до {endRequestTime}:00</span>.
        </p>
        <p style={{ color: '#595959', fontSize: '16px', lineHeight: '28px' }}>
          Спробуйте, будь ласка, через <span style={{ fontWeight: 600, color: '#000' }}>{timeLeft}</span>.
        </p>
      </div>
    );
  }

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} className="public-form">

      {/* 1 ─── Контактна інформація ───────────────── */}
      <div className="form-section-title">Контактна інформація</div>
      <ContactsSection />

      {/* 2 ─── Підрядники та контрагенти ─────────── */}
      <div className="form-section-title">Підрядники та контрагенти</div>
      <div className="form-grid">
        <Form.Item
          name="Account"
          label="ТОВ/ФОП з договором з ТРЦ (ЄДРПОУ)"
          rules={[
            { required: true, message: 'Введіть ЄДРПОУ' },
            { pattern: /^\d{8}$|^\d{10}$/, message: 'ЄДРПОУ має містити 8 або 10 цифр' },
          ]}
        >
          <Input placeholder="00000000" onBlur={handleEdrpouBlur} maxLength={10} />
        </Form.Item>
        <Form.Item name="RespMountingContractor" label="ТОВ / ФОП – підрядна організація">
          <Input placeholder="Назва підрядника" />
        </Form.Item>
      </div>

      {/* 3 ─── Інформація про експозицію ─────────── */}
      <div className="form-section-title">Інформація про експозицію</div>
      <div className="form-grid">
        <Form.Item name="Subject" label="Назва експозиції / активності" rules={[{ required: true, message: 'Заповніть поле' }]}>
          <Input placeholder="Назва" />
        </Form.Item>
        <Form.Item name="SuWorkDescription" label="Локація розміщення в ТРЦ" rules={[{ required: true, message: 'Заповніть поле' }]}>
          <Input placeholder="Рівень, зона" />
        </Form.Item>
        <Form.Item name="placementPeriod" label="Період розміщення" dependencies={['deliveryRange']} rules={[{ required: true, message: 'Оберіть період' }]}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              format="DD.MM.YYYY"
              placeholder={['З якого числа', 'По яке число']}
              disabledDate={disabledPlacementDate}
              showToday={false}
              onChange={() => {
                form.validateFields(['removalRange']).catch(() => {});
              }}
              suffixIcon={<DownOutlined />}
            />
        </Form.Item>
        <Form.Item
          name="Symptoms"
          label="Що планується завезти / вивезти"
          rules={[{ required: true, message: 'Заповніть поле' }]}
        >
          <Input.TextArea rows={2} placeholder="Опис обладнання, кількість, призначення..." />
        </Form.Item>
      </div>

      {/* ─── Так/Ні питання ───────────────────────── */}
      <div className="radio-group-section" style={{ marginTop: '16px' }}>
        <Form.Item
          name="RespStaffOnLocation"
          label="Наявність персоналу на локації"
          rules={[{ required: true, message: 'Оберіть варіант' }]}
        >
          <Radio.Group>
            <Radio value={true}>Так</Radio>
            <Radio value={false}>Ні</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="RespAltitudeWorks"
          label="Висотні роботи"
          rules={[{ required: true, message: 'Оберіть варіант' }]}
        >
          <Radio.Group>
            <Radio value={true}>Так</Radio>
            <Radio value={false}>Ні</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="RespHasLadder"
          label="Наявність драбини"
          rules={[{ required: true, message: 'Оберіть варіант' }]}
        >
          <Radio.Group>
            <Radio value={true}>Так</Radio>
            <Radio value={false}>Ні</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="RespPowerGrid"
          label="Підключення до електромережі"
          rules={[{ required: true, message: 'Оберіть варіант' }]}
        >
          <Radio.Group>
            <Radio value={true}>Так</Radio>
            <Radio value={false}>Ні</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      {/* ─── Додаткова інформація ─────────────────── */}
      <div className="radio-group-section" style={{ marginTop: 16 }}>
        <Form.Item
          name="hasAdditionalInfoChoice"
          label="Чи є додаткова інформація?"
          rules={[{ required: true, message: "Це поле є обов'язковим" }]}
        >
          <Radio.Group onChange={(e) => setHasAdditionalInfo(e.target.value)} value={hasAdditionalInfo}>
            <Radio value={true}>Так</Radio>
            <Radio value={false}>Ні</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      <div style={{ marginTop: 0 }}>
        {hasAdditionalInfo === true && (
          <Form.Item name="Notes" rules={[{ required: true, message: 'Заповніть додаткову інформацію' }]}>
            <Input.TextArea rows={3} placeholder="Вкажіть додаткову інформацію..." />
          </Form.Item>
        )}
      </div>

      {/* ─── Файли ────────────────────────────────── */}
      <div className="upload-section">
        <div className="upload-section-label">Візуалізація (обовʼязково)</div>
        <Upload beforeUpload={handleBeforeUploadVisual} showUploadList={false} multiple accept="image/*,.pdf,.doc,.docx">
          <Button icon={<UploadOutlined />}>Додати файли візуалізації</Button>
        </Upload>
        {visualFileList.length > 0 && (
          <div className="upload-preview-list">
            {visualFileList.map((file) => (
              <div key={file.uid} className="upload-preview-item">
                <FileImageOutlined className="upload-preview-icon" />
                <span className="upload-preview-name">{file.name}</span>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveVisual(file.uid)} className="upload-preview-remove" />
              </div>
            ))}
          </div>
        )}
        {visualFileList.length === 0 && (
          <div className="upload-empty-hint">Завантажте візуалізацію або схему розміщення (кнопка вище)</div>
        )}
      </div>

      <div className="upload-section" style={{ marginTop: '24px' }}>
        <div className="upload-section-label">Фото локації</div>
        <Upload beforeUpload={handleBeforeUploadLocation} showUploadList={false} multiple accept="image/*,.pdf">
          <Button icon={<UploadOutlined />}>Додати фото локації</Button>
        </Upload>
        {locationFileList.length > 0 && (
          <div className="upload-preview-list">
            {locationFileList.map((file) => (
              <div key={file.uid} className="upload-preview-item">
                <FileImageOutlined className="upload-preview-icon" />
                <span className="upload-preview-name">{file.name}</span>
                <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveLocation(file.uid)} className="upload-preview-remove" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 4 ─── Монтаж та демонтаж ────────────────── */}
      <div className="form-section-title">Монтаж та демонтаж</div>

      {/* Монтаж — RangePicker */}
      <Form.Item
        name="deliveryRange"
        label="Завезення / Монтаж (початок — завершення)"
        dependencies={['placementPeriod']}
        rules={[
          { required: true, message: 'Оберіть період монтажу' },
          makeRangeValidator('deliveryRange'),
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || !value[1]) return Promise.resolve();
              const placementPeriod = getFieldValue('placementPeriod');
              if (placementPeriod && placementPeriod[0]) {
                const placementStart = placementPeriod[0].clone().startOf('day');
                if (value[1].isAfter(placementStart)) {
                  return Promise.reject(new Error('Монтаж має завершитися до початку проведення'));
                }
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <DatePicker.RangePicker
          showTime={{
            hideDisabledOptions: true,
            format: 'HH:00',
            showNow: false,
            minuteStep: 60,
          }}
          style={{ width: '100%' }}
          format="DD.MM.YYYY HH:00"
          placeholder={['Початок монтажу', 'Завершення монтажу']}
          disabledDate={disabledDeliveryDate}
          disabledTime={disabledDeliveryTime}
          onCalendarChange={(dates) => { deliveryDatesRef.current = dates; }}
          disabled={!isDeliveryEnabled}
          inputReadOnly
          allowClear
          showToday={false}
          showNow={false}
          onChange={() => {
            form.validateFields(['placementPeriod']).catch(() => {});
          }}
          suffixIcon={<DownOutlined />}
        />
      </Form.Item>

      {/* Демонтаж — RangePicker */}
      <Form.Item
        name="removalRange"
        label="Вивезення / Демонтаж (початок — завершення)"
        dependencies={['deliveryRange', 'placementPeriod']}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              // Якщо поле ще не чіпали — не показуємо жодної помилки
              if (!removalTouched) {
                if (!value || !value[0]) return Promise.resolve();
              }

              // Перевірка обов'язковості
              if (!value || !value[0] || !value[1]) {
                return removalTouched
                  ? Promise.reject(new Error('Оберіть період демонтажу'))
                  : Promise.resolve();
              }

              // Перевірка: кінець після початку та макс. 7 днів
              const [start, end] = value;
              if (end.isSameOrBefore(start)) {
                return Promise.reject(new Error('Час завершення має бути пізніше початку'));
              }
              const diffHours = end.diff(start, 'hours');
              if (diffHours > 7 * 24) {
                return Promise.reject(new Error('Різниця між початком і кінцем демонтажу не може перевищувати 7 днів'));
              }

              // Перевірка: після завершення експозиції
              const placementPeriod = getFieldValue('placementPeriod');
              if (placementPeriod && placementPeriod[1]) {
                const placementEnd = placementPeriod[1].clone().startOf('day');
                if (value[0].isBefore(placementEnd)) {
                  return Promise.reject(new Error('Початок демонтажу має бути після завершення експозиції'));
                }
              }

              // Перевірка: після завершення монтажу
              const deliveryRange = getFieldValue('deliveryRange');
              if (deliveryRange && deliveryRange[1]) {
                if (value[0].isSameOrBefore(deliveryRange[1])) {
                  return Promise.reject(new Error('Початок демонтажу має бути після завершення монтажу'));
                }
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <DatePicker.RangePicker
          showTime={{
            hideDisabledOptions: true,
            format: 'HH:00',
            showNow: false,
            minuteStep: 60,
          }}
          style={{ width: '100%' }}
          format="DD.MM.YYYY HH:00"
          placeholder={['Початок демонтажу', 'Завершення демонтажу']}
          disabledDate={disabledRemovalDate}
          disabledTime={disabledRemovalTime}
          onCalendarChange={(dates) => { removalDatesRef.current = dates; }}
          onChange={() => {
            setRemovalTouched(true);
            form.validateFields(['removalRange']).catch(() => {});
          }}
          disabled={!isRemovalEnabled}
          inputReadOnly
          allowClear
          showToday={false}
          showNow={false}
          suffixIcon={<DownOutlined />}
        />
      </Form.Item>

      {/* ─── Зона завантаження + Авто ─────────────── */}
      <div className="form-grid">
        <LoadingZoneSelect />

        {/* Мультиавто */}
        <div>
          <div className="ant-form-item-label" style={{ paddingBottom: 4 }}>
            <label>Номер авто (паркінг)</label>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {carPlates.map((plate, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input
                  value={plate}
                  onChange={(e) => updateCarPlate(idx, e.target.value.toUpperCase())}
                  placeholder="АА 0000 АА"
                  maxLength={10}
                  style={{ flex: 1 }}
                  status={plate && !CAR_PLATE_PATTERN.test(plate) ? 'error' : ''}
                />
                {carPlates.length > 1 && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => removeCarPlate(idx)}
                  />
                )}
              </div>
            ))}
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={addCarPlate}
              style={{ alignSelf: 'flex-start' }}
            >
              Додати авто
            </Button>
          </div>
        </div>
      </div>

      <div className="form-submit-row">
        <Button type="primary" htmlType="submit" loading={loading} className="form-submit-btn">
          Надіслати звернення
        </Button>
        <div className="recaptcha-disclosure">
          This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.
        </div>
      </div>
    </Form>
  );
};

export default AdvertisingForm;
